import { useState, useEffect, useCallback } from 'react';
import { academicService } from '../services/academicService';
import { useAuth } from '../context/AuthContext';

export const useTimetable = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('weekly');
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchTimetable = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      
      // Determine which API endpoint to call based on user role
      switch (user?.role) {
        case 'student':
        case 'teacher':
        case 'staff':
          // Use my-schedule endpoint for all authenticated users
          response = await academicService.getMyTimetable(params);
          break;
        case 'parent':
          // For parents, we might need to get children's timetables
          // This would need to be implemented based on parent-child relationships
          response = await academicService.getMyTimetable(params);
          break;
        default:
          response = await academicService.getMyTimetable(params);
      }
      
      // Handle different response structures
      const responseData = response.data?.data || response.data || {};
      const timetableData = responseData.timetables || responseData || [];
      setEntries(Array.isArray(timetableData) ? timetableData : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load timetable');
      console.error('Timetable fetch error:', err);
      setEntries([]); // Ensure entries is always an array
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchClassTimetable = useCallback(async (classId, params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await academicService.getClassTimetable(classId, params);
      setEntries(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load class timetable');
      console.error('Class timetable fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const exportTimetable = useCallback(async (format = 'pdf') => {
    try {
      const params = {
        format,
        academicYear: new Date().getFullYear(),
        term: 'First Term' // This should be dynamic
      };
      
      // This would need to be implemented in the backend
      const response = await academicService.exportTimetable(params);
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/calendar' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timetable-${user?.role}-${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export timetable');
      console.error('Export error:', err);
    }
  }, [user]);

  const printTimetable = useCallback(() => {
    const printWindow = window.open('', '_blank');
    const timetableData = entries;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Timetable - ${user?.firstName} ${user?.lastName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .timetable { width: 100%; border-collapse: collapse; }
            .timetable th, .timetable td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .timetable th { background-color: #f2f2f2; }
            .subject { font-weight: bold; }
            .time { color: #666; font-size: 0.9em; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Timetable</h1>
            <p>${user?.firstName} ${user?.lastName} - ${user?.role}</p>
            <p>Academic Year: ${new Date().getFullYear()}</p>
          </div>
          <table class="timetable">
            <thead>
              <tr>
                <th>Day</th>
                <th>Time</th>
                <th>Subject</th>
                <th>Class/Teacher</th>
                <th>Room</th>
              </tr>
            </thead>
            <tbody>
              ${timetableData.map(entry => `
                <tr>
                  <td>${entry.dayOfWeek}</td>
                  <td class="time">${entry.startTime} - ${entry.endTime}</td>
                  <td class="subject">${entry.subjectName}</td>
                  <td>${user?.role === 'student' ? entry.className : entry.teacherName}</td>
                  <td>${entry.room || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  }, [entries, user]);

  const getEntriesForDay = useCallback((day) => {
    return entries.filter(entry => entry.dayOfWeek === day);
  }, [entries]);

  const getEntriesForTime = useCallback((time) => {
    return entries.filter(entry => entry.startTime === time);
  }, [entries]);

  const getUpcomingClasses = useCallback(() => {
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentTime = now.toTimeString().slice(0, 5);
    
    return entries.filter(entry => {
      if (entry.dayOfWeek === currentDay) {
        return entry.startTime > currentTime;
      }
      return true; // Future days
    }).sort((a, b) => {
      if (a.dayOfWeek !== b.dayOfWeek) {
        const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        return dayOrder.indexOf(a.dayOfWeek) - dayOrder.indexOf(b.dayOfWeek);
      }
      return a.startTime.localeCompare(b.startTime);
    });
  }, [entries]);

  useEffect(() => {
    if (user) {
      fetchTimetable();
    }
  }, [user, fetchTimetable]);

  return {
    entries,
    loading,
    error,
    viewMode,
    setViewMode,
    selectedDay,
    setSelectedDay,
    fetchTimetable,
    fetchClassTimetable,
    exportTimetable,
    printTimetable,
    getEntriesForDay,
    getEntriesForTime,
    getUpcomingClasses,
    refetch: fetchTimetable
  };
};
